import { useCloudContext, useConfigContext, useOfflineSWR } from "@WahooFitness/wahoo-offline-mfe";
import { AssociatedDevices } from "@WahooFitness/cloud-client-ts";
import { useMemo } from "react";
import { crux_bolt_type_e, crux_wahoo_product_type_e } from "@WahooFitness/crux-js-display-cfg";
import usePairedDevices from "./usePairedDevices";

export const elementProductBoltMap = {
  [crux_wahoo_product_type_e.ELEMNT_BOLT]: crux_bolt_type_e.BOLT,
  [crux_wahoo_product_type_e.ELEMNT_BOLT2]: crux_bolt_type_e.BOLT_2,
  [crux_wahoo_product_type_e.ELEMNT_BOLT3]: crux_bolt_type_e.BOLT_3,
  [crux_wahoo_product_type_e.ELEMNT_BOLT4]: crux_bolt_type_e.BOLT_4,
  [crux_wahoo_product_type_e.ELEMNT_ROAM]: crux_bolt_type_e.ROAM,
  [crux_wahoo_product_type_e.ELEMNT_ROAM2]: crux_bolt_type_e.ROAM_2,
  [crux_wahoo_product_type_e.ELEMNT_ROAM3]: crux_bolt_type_e.ROAM_3,
  [crux_wahoo_product_type_e.ELEMNT_ROAM4]: crux_bolt_type_e.ROAM_4,
  [crux_wahoo_product_type_e.ELEMNT_ACE]: crux_bolt_type_e.ACE,
  [crux_wahoo_product_type_e.ELEMNT_ACE2]: crux_bolt_type_e.ACE_2,
};

const useAssociatedDevices = () => {
  const { wahooToken } = useConfigContext();
  const { getCloudClient } = useCloudContext();
  const { devices: pairedDevices } = usePairedDevices();

  const AssociatedDevicesClient = useMemo(
    () => getCloudClient(AssociatedDevices),
    [getCloudClient]
  );
  const {
    data: cloudDevices,
    isLoading,
    error,
  } = useOfflineSWR(
    () => (wahooToken ? ["associated_devices", wahooToken] : undefined),
    ([_, wahooToken]) => AssociatedDevicesClient.get(wahooToken)
  );

  const associatedBoltTypes = useMemo(() => {
    const boltProductTypes = Object.keys(elementProductBoltMap).map(Number);
    const cloudBolts = cloudDevices
      ?.filter(
        (device) =>
          device.product_type !== undefined && boltProductTypes.includes(device.product_type)
      )
      .map((device) =>
        device.product_type !== undefined
          ? elementProductBoltMap[device.product_type]
          : crux_bolt_type_e.UNKNOWN
      );
    const pairedBolts = pairedDevices
      ?.filter(
        (device) =>
          device.wahooProductType !== undefined &&
          boltProductTypes.includes(device.wahooProductType)
      )
      .map((device) =>
        device.wahooProductType !== undefined
          ? elementProductBoltMap[device.wahooProductType]
          : crux_bolt_type_e.UNKNOWN
      );
    return Array.from(new Set([...(cloudBolts ?? []), ...(pairedBolts ?? [])]));
  }, [cloudDevices, pairedDevices]);

  const hasAssociated1st2ndGenBolt = useMemo(
    () =>
      associatedBoltTypes?.includes(crux_bolt_type_e.BOLT) ||
      associatedBoltTypes?.includes(crux_bolt_type_e.BOLT_2),
    [associatedBoltTypes]
  );

  const hasAssociated1st2ndGenRoam = useMemo(
    () =>
      associatedBoltTypes?.includes(crux_bolt_type_e.ROAM) ||
      associatedBoltTypes?.includes(crux_bolt_type_e.ROAM_1_1) ||
      associatedBoltTypes?.includes(crux_bolt_type_e.ROAM_2),
    [associatedBoltTypes]
  );

  const hasAssociated3rdGenBolt = useMemo(
    () => associatedBoltTypes?.includes(crux_bolt_type_e.BOLT_3),
    [associatedBoltTypes]
  );

  const hasAssociated3rdGenRoam = useMemo(
    () => associatedBoltTypes?.includes(crux_bolt_type_e.ROAM_3),
    [associatedBoltTypes]
  );

  const hasAssociatedAce = useMemo(
    () => associatedBoltTypes?.includes(crux_bolt_type_e.ACE),
    [associatedBoltTypes]
  );

  const hasAssociated4thGenElemnt = useMemo(
    () =>
      associatedBoltTypes?.includes(crux_bolt_type_e.BOLT_4) ||
      associatedBoltTypes?.includes(crux_bolt_type_e.ROAM_4) ||
      associatedBoltTypes?.includes(crux_bolt_type_e.ACE_2),
    [associatedBoltTypes]
  );

  const hasAssociatedElemnt = useMemo(
    () =>
      hasAssociated1st2ndGenBolt ||
      hasAssociated1st2ndGenRoam ||
      hasAssociated3rdGenBolt ||
      hasAssociated3rdGenRoam ||
      hasAssociatedAce ||
      hasAssociated4thGenElemnt,
    [
      hasAssociated1st2ndGenBolt,
      hasAssociated1st2ndGenRoam,
      hasAssociated3rdGenBolt,
      hasAssociated3rdGenRoam,
      hasAssociatedAce,
      hasAssociated4thGenElemnt,
    ]
  );

  const hasPairedGen3ComputerWithSpeaker = useMemo(
    () =>
      !!associatedBoltTypes?.some(
        (boltType) => boltType === crux_bolt_type_e.ROAM_3 || boltType === crux_bolt_type_e.ACE
      ),
    [associatedBoltTypes]
  );

  return {
    cloudDevices,
    associatedDevicesIsLoading: isLoading,
    associatedDevicesError: error,
    associatedBoltTypes,
    hasAssociated1st2ndGenBolt,
    hasAssociated1st2ndGenRoam,
    hasAssociated3rdGenBolt,
    hasAssociated3rdGenRoam,
    hasAssociatedAce,
    hasAssociatedElemnt,
    hasPairedGen3ComputerWithSpeaker,
  };
};

export default useAssociatedDevices;
